import React from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../../lib/utils';
import useBottomBarStore from '../../store/bottom-bar';
import { useSidebar } from '../../context/SidebarProvider';
import { isPresenterMode as isUrlPresenterMode } from '../../utils/isPresenterMode';

interface FixedBottomBarProps {
  children: React.ReactNode;
  visible: boolean;
  className?: string;
}

export function FixedBottomBar({ children, visible, className }: FixedBottomBarProps) {
  const { sidebarWidth } = useSidebar();
  // Only the URL demo toggle hides the sidebar; Read-Only users still see it (see PrivateApp),
  // so the bar must align to its right edge for them too.
  const isPresenterMode = isUrlPresenterMode();

  const showBottomBar = useBottomBarStore((state) => state.showBottomBar);
  const hideBottomBar = useBottomBarStore((state) => state.hideBottomBar);

  // The portal is always mounted and `visible` only toggles classes, so the count tracks the prop
  // rather than the mount. A counter because a route can render more than one bar.
  React.useEffect(() => {
    if (!visible) {
      return;
    }
    showBottomBar();
    return hideBottomBar;
  }, [visible, showBottomBar, hideBottomBar]);

  return ReactDOM.createPortal(
    <div
      className={cn(
        // No launcher clearance: the App Assistant launcher hides while a bar is visible.
        'fixed bottom-0 z-[50] border-t border-t-neutral-100 bg-seasalt px-[31px] py-5 transition-all',
        visible ? 'opacity-100' : 'pointer-events-none hidden opacity-0',
        className,
      )}
      style={{
        left: isPresenterMode ? 0 : sidebarWidth,
        // This bar is portaled to body, so the app shell's page inset cannot reach it —
        // without this its primary actions sit under the assistant drawer.
        right: 'var(--app-assistant-inset, 0px)',
        transition: 'left 0.25s ease-in-out, right 0.25s ease-in-out, opacity 0.2s ease',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
